/**
 * Local message search — minisearch on decrypted client index only (Engine 12).
 * @see https://github.com/lucaong/minisearch
 */

import MiniSearch from 'minisearch';
import { setConversationPreview } from '../chat/home/conversationPreviews';

const indexes = new Map();

function getIndex(conversationId) {
  if (!indexes.has(conversationId)) {
    indexes.set(
      conversationId,
      new MiniSearch({
        fields: ['text'],
        storeFields: ['id', 'text', 'sender_id', 'created_at'],
        searchOptions: { boost: { text: 2 }, prefix: true, fuzzy: 0.2 },
      })
    );
  }
  return indexes.get(conversationId);
}

export function indexMessage(conversationId, message) {
  if (!conversationId || !message?.id) return;

  // Local list preview (plaintext only — already decrypted on this device)
  if (message.text) {
    setConversationPreview(conversationId, {
      text: message.text,
      at: message.created_at,
      kind: 'text',
    });
  } else if (message.attachment) {
    setConversationPreview(conversationId, {
      kind: message.attachment?.kind === 'voice' ? 'voice' : 'attachment',
      at: message.created_at,
    });
  } else if (message.poll) {
    setConversationPreview(conversationId, {
      kind: 'poll',
      at: message.created_at,
    });
  }

  if (!message.text) return;
  const idx = getIndex(conversationId);
  try {
    idx.add({
      id: message.id,
      text: message.text,
      sender_id: message.sender_id,
      created_at: message.created_at,
    });
  } catch {
    idx.discard(message.id);
    idx.add({
      id: message.id,
      text: message.text,
      sender_id: message.sender_id,
      created_at: message.created_at,
    });
  }
}

export function indexMessages(conversationId, messages) {
  const idx = getIndex(conversationId);
  idx.removeAll();
  for (const m of messages) {
    if (m?.id) {
      indexMessage(conversationId, m);
    }
  }
}

export function searchMessages(conversationId, query, { limit = 20 } = {}) {
  if (!query?.trim()) return [];
  const idx = getIndex(conversationId);
  return idx.search(query.trim()).slice(0, limit);
}

export function removeMessageFromIndex(conversationId, messageId) {
  if (!conversationId || !messageId) return;
  const idx = indexes.get(conversationId);
  if (!idx) return;
  try {
    idx.discard(messageId);
  } catch {
    // not indexed
  }
}


export function clearAllIndexes() {
  indexes.clear();
}

const INDEX_OPTIONS = {
  fields: ['text'],
  storeFields: ['id', 'text', 'sender_id', 'created_at'],
  searchOptions: { boost: { text: 2 }, prefix: true, fuzzy: 0.2 },
};

export function exportAllIndexes() {
  const out = {};
  for (const [conversationId, idx] of indexes) {
    try {
      out[conversationId] = idx.toJSON();
    } catch {
      /* skip broken index */
    }
  }
  return out;
}

export function importAllIndexes(data) {
  if (!data || typeof data !== 'object') return 0;
  clearAllIndexes();
  let count = 0;
  for (const [conversationId, json] of Object.entries(data)) {
    if (!conversationId || !json) continue;
    try {
      const serialized = typeof json === 'string' ? json : JSON.stringify(json);
      const idx = MiniSearch.loadJSON(serialized, INDEX_OPTIONS);
      indexes.set(conversationId, idx);
      count += 1;
    } catch {
      /* skip invalid snapshot */
    }
  }
  return count;
}